// ---------------------------------------------------------------------------
// compliance-region.zh.ts — Chinese translations for all 7 compliance regions
// ---------------------------------------------------------------------------
import type { ComplianceRegionText } from './compliance-region.content';

export const COMPLIANCE_ZH: Record<string, ComplianceRegionText> = {
  'eu-uk': {
    region: 'EU / 英国',
    title: 'EU与英国酒店合规软件',
    subtitle: '完整的GDPR合规、EU增值税自动化、PSD2/SCA支付安全和ePrivacy Cookie同意集成到每个HotelStack.io工作流程中。',
    features: [
      { title: 'GDPR数据保护', description: '自动数据保留策略、删除权工作流程、客人数据导出（DSAR）以及所有客人接触点的同意管理。' },
      { title: 'EU增值税自动化', description: '涵盖全部27个EU成员国和英国的住宿及附属服务多税率增值税计算。' },
      { title: 'PSD2 / SCA合规', description: '在线支付强客户认证、3D Secure 2.0集成和SCA豁免处理。' },
      { title: '隐私与Cookie同意', description: 'PECR和ePrivacy合规Cookie横幅、精细同意追踪和分析选择加入/退出。' },
      { title: '警察登记表', description: '自动生成法国、意大利、西班牙和葡萄牙的国家专用格式警察登记表。' },
      { title: '多语言多币种', description: '支持12种欧洲语言界面。EUR、GBP、CHF、SEK、NOK实时汇率计费。' },
    ],
    taxDetails: [
      { name: '标准增值税（英国）', rate: '20%', description: '适用于住宿和大部分酒店服务。' },
      { name: '优惠税率（法国）', rate: '10%', description: '法国住宿服务适用的优惠TVA税率。' },
      { name: '优惠税率（德国）', rate: '7%', description: '德国短期住宿适用的优惠增值税率。' },
      { name: '优惠税率（西班牙）', rate: '10%', description: '西班牙酒店服务适用的优惠IVA税率。' },
      { name: '优惠税率（意大利）', rate: '10%', description: '意大利住宿服务适用的优惠IVA税率。' },
      { name: '住宿税（浮动）', rate: '按城市', description: '住宿税/旅游税 - 按城市自动计算。' },
    ],
    guestRegistration: 'HotelStack.io自动化EU/英国各辖区的客人登记。为法国、意大利、西班牙和葡萄牙生成警察登记表。入住时通过护照和身份证扫描采集所需身份信息。',
    dataProtection: '完整的GDPR第17条删除权工作流程、第15条数据主体访问请求、第30条处理活动记录和数据保护影响评估。英国GDPR与EU GDPR双重合规。',
    faqItems: [
      { question: 'HotelStack.io如何处理酒店的GDPR合规？', answer: '我们的平台包括自动数据保留策略、删除权、客人数据导出、同意管理和处理记录。所有客人数据加密存储，提供EU数据驻留选项。' },
      { question: 'HotelStack.io支持PSD2强认证吗？', answer: '是的。所有在线支付通过3D Secure 2.0实现SCA合规，并提供低风险交易豁免处理。' },
      { question: 'EU各国增值税率如何管理？', answer: 'HotelStack.io维护所有EU成员国和英国的最新增值税率数据库，自动更新。' },
      { question: '能为多个国家生成警察登记表吗？', answer: '是的。入住模块为法国、意大利、西班牙和葡萄牙生成国家专用警察登记表。' },
    ],
  },
  'united-states': {
    region: '美国',
    title: '美国酒店税务与合规软件',
    subtitle: '各州税务自动化、ADA无障碍追踪、PCI DSS一级支付安全、全美各辖区住宿税申报。',
    features: [
      { title: '州和地方税自动化', description: '全美50个州的州销售税、住宿税和旅游改善区费用自动计算。' },
      { title: 'ADA无障碍合规', description: 'ADA合规客房库存追踪、无障碍报告和WCAG 2.1 AA合规保障。' },
      { title: 'PCI DSS一级', description: '令牌化卡片存储、点对点加密和自动PCI合规报告。' },
      { title: '住宿税申报', description: '生成面向市、县和州当局的申报就绪住宿税申报表。' },
      { title: '劳动法合规', description: '各州加班规则、最低工资追踪和工会劳动排班。' },
      { title: 'CCPA / 隐私法', description: '加州消费者隐私法及各州隐私法合规，内置退出和数据删除工作流程。' },
    ],
    taxDetails: [
      { name: '州销售税', rate: '0% - 7.25%', description: '按州而异。适用于客房费用和应税服务。' },
      { name: '市住宿税', rate: '2% - 15%', description: '客房收入的市级临时住宿税。' },
      { name: '县税', rate: '0.5% - 5%', description: '适用地区的县级住宿税。' },
      { name: '旅游区费', rate: '1% - 3%', description: '主要城市的旅游改善区费用。' },
      { name: '会议费', rate: '$1 - $5/晚', description: '用于会议中心建设的固定每晚费用。' },
      { name: '度假村费用披露', rate: '浮动', description: '依据FTC指南的强制性度假村费用披露。' },
    ],
    guestRegistration: '美国酒店客人登记按各州要求在物业层面管理。HotelStack.io采集客人身份和车辆信息，生成符合州酒店业法规的登记卡。',
    dataProtection: 'HotelStack.io遵守CCPA（加州）、CDPA（弗吉尼亚）、CPA（科罗拉多）、CTDPA（康涅狄格）和UCPA（犹他）。内置退出和数据删除工作流程。SOC 2 Type II认证基础设施。',
    faqItems: [
      { question: 'HotelStack.io如何处理各州不同的税率？', answer: '税务引擎维护全美各辖区州和地方税率的综合数据库，自动更新。' },
      { question: 'HotelStack.io支持ADA合规追踪吗？', answer: '是的。追踪ADA合规客房数量和无障碍功能，确保预订引擎符合WCAG 2.1 AA标准。' },
      { question: 'PCI DSS合规如何维护？', answer: '所有支付卡数据在输入时即进行令牌化。通过年度审计维护PCI DSS一级认证。' },
      { question: '能生成住宿税申报表吗？', answer: '是的。HotelStack.io为全美主要辖区生成申报就绪的住宿税申报表。' },
    ],
  },
  'uae-middle-east': {
    region: 'UAE / 中东',
    title: 'UAE与中东酒店合规软件',
    subtitle: '旅游迪拉姆费用自动化、含TRN发票的5%增值税、DTCM客人登记和面向GCC运营的阿拉伯语RTL界面。',
    features: [
      { title: '旅游迪拉姆费用', description: '基于酒店星级的自动旅游迪拉姆计算（每房每晚7-20 AED）。DTCM就绪报告。' },
      { title: '5%增值税与TRN发票', description: 'FTA合规的5%增值税计算，含税务登记号（TRN）。' },
      { title: 'DTCM客人登记', description: '面向迪拜旅游与商业营销部eServices门户的自动客人数据采集。' },
      { title: '阿拉伯语RTL界面', description: 'PMS和客人预订引擎的完整阿拉伯语RTL界面。' },
      { title: 'PDPL数据保护', description: 'UAE个人数据保护法合规，含处理记录和同意管理。' },
      { title: '市政合规', description: '支持阿布扎比、沙迦和哈伊马角旅游局要求。' },
    ],
    taxDetails: [
      { name: 'UAE增值税', rate: '5%', description: '对包括客房收入在内的应税供应征收的联邦税。' },
      { name: '旅游迪拉姆（五星）', rate: '20 AED/房/晚', description: '迪拜五星级酒店的高级费率。' },
      { name: '旅游迪拉姆（四星）', rate: '15 AED/房/晚', description: '迪拜四星级酒店的标准费率。' },
      { name: '旅游迪拉姆（三星）', rate: '10 AED/房/晚', description: '迪拜三星级酒店的费率。' },
      { name: '旅游迪拉姆（一至二星）', rate: '7 AED/房/晚', description: '迪拜一至二星级酒店的基本费率。' },
      { name: '市政费', rate: '7%（迪拜）', description: '适用于客房收入的迪拜市政费。' },
    ],
    guestRegistration: 'HotelStack.io在入住时采集UAE身份证、护照和签证信息，提交至DTCM eServices。自动追踪客人国籍和旅游统计数据。',
    dataProtection: '遵守2021年第45号联邦法令（个人数据保护法）。处理记录、合法利益评估和UAE数据中心的数据驻留选项。',
    faqItems: [
      { question: 'HotelStack.io如何计算迪拜旅游迪拉姆？', answer: '根据酒店星级自动应用正确的旅游迪拉姆费率。月度DTCM报告一键生成。' },
      { question: '支持含TRN的增值税发票吗？', answer: '是的。所有发票包含TRN、5%增值税明细，符合FTA要求。' },
      { question: '阿拉伯语界面是完整的RTL实现吗？', answer: '是的。完整的阿拉伯语RTL界面覆盖PMS、预订引擎、发票和报告。' },
      { question: '支持迪拜以外的酋长国合规吗？', answer: '是的。支持阿布扎比旅游文化局、沙迦旅游局和哈伊马角旅游局的要求。' },
    ],
  },
  'thailand-sea': {
    region: '泰国 / 东南亚',
    title: '泰国与东南亚酒店合规软件',
    subtitle: '泰国7%增值税自动化、TM30外国人报告、PDPA合规和TAT报告集成到HotelStack.io工作流程中。',
    features: [
      { title: '泰国7%增值税', description: '客房收入和服务的7%增值税自动计算，税务局格式税务发票生成。' },
      { title: 'TM30外国人报告', description: '自动化外国客人TM30向移民局报告，24小时内提交。' },
      { title: 'PDPA合规', description: '2019年泰国个人数据保护法合规，同意管理和数据主体权利工作流程。' },
      { title: 'TAT统计报告', description: '泰国国家旅游局格式的住宿统计和客人人口统计报告。' },
      { title: '泰语界面', description: 'PMS和客人端的完整泰语本地化。' },
      { title: '东南亚扩展', description: '支持越南、马来西亚、印度尼西亚和菲律宾的税务和报告框架。' },
    ],
    taxDetails: [
      { name: '泰国增值税', rate: '7%', description: '适用于客房费用和酒店服务的增值税。' },
      { name: '特定营业税', rate: '3.3%', description: '适用于符合条件的房产交易。' },
      { name: '预扣税', rate: '1-5%', description: '适用于企业间服务付款的预扣税。' },
      { name: '地方发展税', rate: '浮动', description: '泰国地方政府的物业和土地评估税。' },
      { name: '印花税', rate: '浮动', description: '适用于合同和法律文件的印花税。' },
      { name: '旅游推广基金', rate: '拟议中', description: '支持泰国旅游推广的拟议税种。' },
    ],
    guestRegistration: 'HotelStack.io在入住时自动化护照和签证扫描，将外国客人的TM30数据直接提交至移民局系统。同时支持泰国公民身份证扫描。',
    dataProtection: '遵守2019年泰国个人数据保护法（PDPA）。同意记录、数据主体访问请求、数据传输影响评估以及必要时的数据保护官（DPO）任命支持。',
    faqItems: [
      { question: 'HotelStack.io如何处理TM30报告？', answer: '外国客人入住时自动采集TM30数据并直接提交至移民局系统。遵守24小时提交期限。' },
      { question: '支持泰国增值税税务发票吗？', answer: '是的。所有发票包含符合税务局要求的7%增值税明细。' },
      { question: 'PDPA合规如何管理？', answer: '包含同意记录、数据主体权利工作流程、处理记录和DPO支持的完整PDPA合规工具包。' },
      { question: '支持泰国以外的东南亚国家吗？', answer: '是的。支持越南、马来西亚、印度尼西亚和菲律宾的税务和报告框架。' },
    ],
  },
  'australia': {
    region: '澳大利亚',
    title: '澳大利亚酒店合规软件',
    subtitle: '10% GST自动化、BAS申报、APR合规客人登记和澳大利亚隐私原则集成到HotelStack.io中。',
    features: [
      { title: '10% GST自动化', description: '住宿和服务的GST自动计算，税务发票和BAS报告生成。' },
      { title: 'BAS申报', description: '自动生成商业活动报表（BAS），符合ATO要求。' },
      { title: '客人登记', description: '符合澳大利亚联邦和州法规的客人身份验证和记录保存。' },
      { title: '隐私法合规', description: '1988年澳大利亚隐私法和APP合规，含数据泄露通知义务。' },
      { title: '公平工作合规', description: '酒店业裁定、罚款率计算和劳动标准合规。' },
      { title: '多州支持', description: '支持NSW、VIC、QLD、SA、WA和TAS各州法规和许可要求。' },
    ],
    taxDetails: [
      { name: 'GST', rate: '10%', description: '适用于住宿费用和大部分酒店服务的商品与服务税。' },
      { name: '葡萄酒平衡税', rate: '29%', description: '适用于迷你吧和餐厅葡萄酒的平衡税。' },
      { name: '附加福利税', rate: '47%', description: '适用于员工住宿补贴的FBT。' },
      { name: '市政税', rate: '浮动', description: '地方政府物业税率。' },
      { name: '土地税', rate: '浮动', description: '超过起征点的商业物业州土地税。' },
      { name: '旅游税', rate: '浮动', description: '主要旅游地区的州和地方旅游征税。' },
    ],
    guestRegistration: 'HotelStack.io自动化符合澳大利亚州和联邦要求的客人登记。护照扫描、签证验证和澳大利亚驾照认证集成到入住工作流程中。',
    dataProtection: '遵守1988年隐私法、澳大利亚隐私原则（APP）和强制数据泄露通知（NDB）制度。实施数据保留策略、访问和更正权以及跨境数据传输保护。',
    faqItems: [
      { question: 'HotelStack.io如何处理澳大利亚GST？', answer: '自动计算住宿费用和服务的10% GST，生成ATO合规税务发票和BAS摘要。' },
      { question: '支持BAS申报吗？', answer: '是的。生成汇总GST征收额、抵扣和调整的BAS就绪报告。' },
      { question: '如何确保澳大利亚隐私法合规？', answer: '客人数据处理遵守所有APP。包含数据泄露通知工作流程、访问请求处理和数据保留策略自动化。' },
      { question: '支持公平工作裁定计算吗？', answer: '是的。自动计算酒店业裁定的罚款率、加班费和公共假日津贴。' },
    ],
  },
  'japan': {
    region: '日本',
    title: '日本酒店合规软件',
    subtitle: '10%消费税自动化、旅馆业法合规、住宿税计算和合格发票制度集成到HotelStack.io中。',
    features: [
      { title: '10%消费税', description: '住宿和餐饮服务消费税自动计算。支持8%优惠税率。合格发票制度合规。' },
      { title: '旅馆业法合规', description: '基于旅馆业法和各地方条例的营业许可和运营标准合规支持。' },
      { title: '住宿税', description: '东京都、大阪府、京都市等各地方政府住宿税自动计算和收取。' },
      { title: '合格发票制度', description: '含合格发票发行事业者登记号的发票生成。支持2023年制度。' },
      { title: '外国住客名簿', description: '外国住客的护照号码、国籍等信息记录，支持向辖区警察署报告。' },
      { title: '日语界面', description: 'PMS全面日语本地化。支持日本式日期和货币格式。' },
    ],
    taxDetails: [
      { name: '消费税（标准）', rate: '10%', description: '适用于住宿费用和大部分酒店服务。' },
      { name: '消费税（优惠税率）', rate: '8%', description: '适用于外带食品饮料的优惠税率。' },
      { name: '住宿税（东京都）', rate: '100-200日元/晚', description: '根据房费的东京都住宿税。' },
      { name: '住宿税（大阪府）', rate: '100-300日元/晚', description: '根据房费的大阪府住宿税。' },
      { name: '住宿税（京都市）', rate: '200-1000日元/晚', description: '根据房费的京都市住宿税。' },
      { name: '入汤税', rate: '150日元/人', description: '适用于温泉设施的入汤税。' },
    ],
    guestRegistration: 'HotelStack.io自动化基于旅馆业法的住客名簿创建。记录外国客人的护照和国籍信息，生成辖区警察署报告数据。同时支持日本公民身份验证。',
    dataProtection: '遵守个人信息保护法（APPI）。实施个人数据安全管理措施、披露/更正/停止使用请求处理以及跨境数据传输同意获取工作流程。',
    faqItems: [
      { question: 'HotelStack.io如何处理日本消费税？', answer: '自动计算10%标准税率和8%优惠税率，生成合格发票制度合规的合格发票。' },
      { question: '支持各地方政府住宿税吗？', answer: '是的。根据房费档位自动计算和收取东京都、大阪府、京都市等各地方政府的住宿税。' },
      { question: '外国住客报告如何进行？', answer: '入住时通过护照扫描采集护照号码、国籍等信息，自动生成警察报告数据。' },
      { question: '支持合格发票制度吗？', answer: '是的。自动生成含合格发票发行事业者登记号的合格发票，同时满足保存要求。' },
    ],
  },
  'india': {
    region: '印度',
    title: '印度酒店合规软件',
    subtitle: 'GST分档税制、C-Form/FRRO申报、FHRAI报告和印度数据保护法合规集成到HotelStack.io中。',
    features: [
      { title: 'GST分档税制', description: '基于房价档位的GST税率自动应用（5%/12%/18%）。GSTR申报就绪报告生成。' },
      { title: 'C-Form申报', description: '外国客人C-Form（表格C）自动生成，FRRO/FRO电子申报支持。' },
      { title: 'FHRAI报告', description: '印度酒店与餐厅协会联盟格式的统计和运营报告。' },
      { title: '印地语界面', description: 'PMS全面印地语本地化。多州语言支持。' },
      { title: 'DPDPA合规', description: '2023年数字个人数据保护法合规，同意管理和数据处理记录。' },
      { title: '州级许可', description: '各州旅游局、消防部门、卫生部门和市政许可要求追踪。' },
    ],
    taxDetails: [
      { name: 'GST（7500卢比以下）', rate: '12%', description: '适用于每晚7,500卢比以下客房的GST税率。' },
      { name: 'GST（7500卢比以上）', rate: '18%', description: '适用于每晚7,500卢比以上客房的GST税率。' },
      { name: 'GST（餐饮）', rate: '5%', description: '适用于非空调餐厅餐饮服务。' },
      { name: 'GST（餐饮-空调）', rate: '5%含ITC', description: '适用于空调餐厅餐饮服务。' },
      { name: 'TCS（源头征税）', rate: '1%', description: '适用于超过特定金额交易的源头征税。' },
      { name: '地方税', rate: '浮动', description: '州和市级额外征税（替代奢侈税）。' },
    ],
    guestRegistration: 'HotelStack.io自动化外国客人C-Form生成和FRRO/FRO电子申报。支持护照和签证扫描，遵守12小时申报期限。同时支持印度公民身份证（Aadhaar/选民证）验证。',
    dataProtection: '遵守2023年数字个人数据保护法（DPDPA）。同意获取和记录、数据主体权利工作流程、数据受托人义务履行以及跨境数据传输限制应对。',
    faqItems: [
      { question: 'HotelStack.io如何处理印度GST分档？', answer: '基于房价档位自动应用GST税率（12%/18%），生成GSTR-1和GSTR-3B申报就绪报告。' },
      { question: '支持C-Form和FRRO申报吗？', answer: '是的。外国客人入住时自动生成C-Form，支持向FRRO/FRO门户电子申报。' },
      { question: 'DPDPA合规如何管理？', answer: '提供同意管理、数据主体权利工作流程、处理记录和数据受托人义务履行的完整工具包。' },
      { question: '支持州级许可追踪吗？', answer: '是的。追踪各州旅游局、消防部门、卫生部门和市政许可的续期日期和要求。' },
    ],
  },
};
